import useForm from 'pages/components/Form/useForm';
import FormFields from 'pages/components/FormFields';
import RegistrationFormConfig from './RegistrationFormConfig';

import { Grid2 } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import Countries from 'pages/config/Countries';
import { useTranslation } from 'react-i18next';
import { RegisterNewCreditor } from 'Application/User/RegisterService';
import { CreateUserRequest } from 'Domain/User/Dto/CreateUserRequest';
import { eventDispatcher } from 'pages/Hooks/useEventListener';
import { TRIGGER_DIALOG } from 'pages/constants/EventKeys';
import { useNavigate } from 'react-router-dom';
import CityInput from 'pages/components/Molecules/CityInput';
import { getUrl, LOGIN } from 'Infrastructure/config/routesConfig';

export default function RegisterForm() {
  const {
    errors,
    values,
    options,
    handleInputChange,
    isValidForm,
    formatErrorObject,
    Form,
  } = useForm(RegistrationFormConfig, true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const navigate = useNavigate();

  function handleSubmit(e: any) {
    if (isValidForm()) {
      RegisterNewCreditor(getCreateNewUserRequest(values)).then((res) =>
        eventDispatcher(TRIGGER_DIALOG, getDialogContent(navigate)),
      );
    }
  }

  const phonePrefix =
    typeof values.country === 'string' && values.country in Countries
      ? Countries[values.country].phonePrefix
      : '';

  return (
    <Form onSubmit={handleSubmit}>
      <Grid2
        display={'grid'}
        gridTemplateColumns={isMobile ? '1fr' : '1fr 1fr'}
        gap={2}
        container
      >
        <FormFields.RadioGroup
          name="gender"
          value={values.gender}
          onChange={handleInputChange}
          items={options.gender}
          required
          {...formatErrorObject(errors.gender)}
        />
        <FormFields.Select
          name="language"
          value={values.language}
          onChange={handleInputChange}
          options={options.language}
          required
          {...formatErrorObject(errors.language)}
        />
        <FormFields.TextInput
          name="firstName"
          value={values.firstName}
          onChange={handleInputChange}
          required
          {...formatErrorObject(errors.firstName)}
        />
        <FormFields.TextInput
          name="lastName"
          value={values.lastName}
          onChange={handleInputChange}
          required
          {...formatErrorObject(errors.lastName)}
        />
        <FormFields.Select
          name="profession"
          value={values.profession}
          onChange={handleInputChange}
          options={options.profession}
          required
          {...formatErrorObject(errors.profession)}
        />
        <FormFields.Select
          name="specialty"
          value={values.specialty}
          onChange={handleInputChange}
          disabled={(options.specialty instanceof Function
              ? options.specialty(values.profession)
              : []).length == 0}
          options={
            options.specialty instanceof Function
              ? options.specialty(values.profession)
              : []
          }
          {...formatErrorObject(errors.language)}
        />
        <FormFields.Select
          name="country"
          value={values.country}
          onChange={handleInputChange}
          options={options.country}
          required
          {...formatErrorObject(errors.country)}
        />
        <CityInput
          name="city"
          country={values.country}
          value={values.city}
          onChange={handleInputChange}
          isMobile={isMobile}
          errors={formatErrorObject(errors.city)}
        />
        <Grid2
          display={'grid'}
          gridTemplateColumns={'1fr 1fr'}
          gap={1}
          container
        >
          <FormFields.TextInput
            name="boxNumber"
            value={values.boxNumber}
            onChange={handleInputChange}
            required
            {...formatErrorObject(errors.boxNumber)}
          />
          <FormFields.TextInput
            name="box"
            value={values.box}
            onChange={handleInputChange}
            {...formatErrorObject(errors.box)}
          />
        </Grid2>
        <FormFields.TextInput
          name="address"
          value={values.address}
          onChange={handleInputChange}
          required
          {...formatErrorObject(errors.address)}
        />
        <FormFields.Phone
          name="phone"
          value={values.phone}
          prefix={phonePrefix}
          onChange={handleInputChange}
          {...formatErrorObject(errors.phone)}
        />
        <FormFields.Phone
          name="mobile"
          value={values.mobile}
          prefix={phonePrefix}
          onChange={handleInputChange}
          mobile={1}
          required
          {...formatErrorObject(errors.mobile)}
        />
        <FormFields.Email
          name="email"
          value={values.email}
          onChange={handleInputChange}
          required
          {...formatErrorObject(errors.email)}
        />
        <FormFields.Email
          name="emailConfirmation"
          value={values.emailConfirmation}
          onChange={handleInputChange}
          onPaste={(e: any) => {
            e.preventDefault();
          }}
          required
          {...formatErrorObject(errors.emailConfirmation)}
        />
      </Grid2>
      <FormFields.Button
        type="submit"
        label={t('FORM.SUBMIT.create_account')}
        fullWidth
        sx={{ mt: 2 }}
      />
    </Form>
  );
}

function getDialogContent(navigate: Function) {
  function handleClick() {
    navigate(getUrl(LOGIN));
  }
  const actions = [
    {
      label: 'login',
      variant: 'outlined',
      color: 'success',
      action: handleClick,
    },
  ];
  return {
    type: 'register',
    actions: actions,
  };
}

function getCreateNewUserRequest(values: any) {
  const createUserRequest = new CreateUserRequest(
    values.email,
    values.gender,
    values.language,
    values.firstName,
    values.lastName,
    values.profession,
    values.specialty,
    values.country,
    values.city.label,
    values.city.value,
    values.boxNumber,
    values.box,
    values.address,
    values.phone,
    values.mobile,
  );

  return createUserRequest;
}
